import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useAdmin } from '../context/AdminContext';
import logo from '../assets/images/LogoBlack.webp';

const EMAIL_REGEX = /\S+@\S+\.\S+/;

const ForgotPassword = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [step, setStep] = useState('request');
  const [email, setEmail] = useState(location.state?.email || '');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [requestLoading, setRequestLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const { requestPasswordResetOtp, verifyPasswordResetOtp } = useAdmin();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    const trimmedEmail = email.trim();

    if (!EMAIL_REGEX.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    if (step === 'request') {
      setRequestLoading(true);

      try {
        const result = await requestPasswordResetOtp(trimmedEmail);

        if (result.success) {
          setStep('verify');
          setSuccessMessage(result.message || `We sent a 6-digit OTP to ${trimmedEmail}.`);
        } else {
          setError(result.error || 'Failed to send OTP');
        }
      } finally {
        setRequestLoading(false);
      }

      return;
    }

    const trimmedOtp = otp.trim();
    const trimmedPassword = newPassword.trim();

    if (!/^\d{6}$/.test(trimmedOtp)) {
      setError('Please enter the 6-digit OTP code.');
      return;
    }

    if (!trimmedPassword) {
      setError('Please enter a new password.');
      return;
    }

    if (trimmedPassword.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    if (trimmedPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setVerifyLoading(true);

    try {
      const result = await verifyPasswordResetOtp(trimmedEmail, trimmedOtp, trimmedPassword);

      if (result.success) {
        setStep('done');
        setSuccessMessage(result.message || 'Password reset successfully.');
        setOtp('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setError(result.error || 'Failed to verify OTP');
      }
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setError('');
    setSuccessMessage('');
    setRequestLoading(true);

    try {
      const result = await requestPasswordResetOtp(email.trim());

      if (result.success) {
        setSuccessMessage(result.message || `We sent a new OTP to ${email.trim()}.`);
      } else {
        setError(result.error || 'Failed to resend OTP');
      }
    } finally {
      setRequestLoading(false);
    }
  };

  const handleChangeEmail = () => {
    setStep('request');
    setError('');
    setSuccessMessage('');
    setOtp('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleBackToLogin = () => {
    navigate('/login', { state: { email: email.trim() } });
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            border: '1px solid #e0e0e0',
          }}
        >
          <Box
            sx={{
              mb: 3,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <img
              src={logo}
              alt="Rallys Logo"
              style={{
                maxWidth: '200px',
                height: 'auto',
                objectFit: 'contain',
              }}
            />
          </Box>

          <Typography component="h1" variant="h5" sx={{ mb: 1, fontWeight: 'bold', color: '#000' }}>
            Reset Password
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            {step === 'request'
              ? 'Enter your email address and we will send a verification code.'
              : 'Enter the code from your email and choose a new password.'}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            {step !== 'done' && (
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                  setSuccessMessage('');
                }}
                disabled={requestLoading || verifyLoading || step === 'verify'}
              />
            )}

            {step === 'verify' && (
              <>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="otp"
                  label="OTP Code"
                  name="otp"
                  autoComplete="one-time-code"
                  inputProps={{ maxLength: 6 }}
                  value={otp}
                  onChange={(e) => {
                    setOtp(e.target.value);
                    setError('');
                  }}
                  disabled={requestLoading || verifyLoading}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="newPassword"
                  label="New Password"
                  name="newPassword"
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setError('');
                  }}
                  disabled={requestLoading || verifyLoading}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="confirmPassword"
                  label="Confirm New Password"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setError('');
                  }}
                  disabled={requestLoading || verifyLoading}
                />
              </>
            )}

            {step !== 'done' ? (
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 2, mb: 1 }}
                disabled={requestLoading || verifyLoading}
              >
                {requestLoading || verifyLoading ? (
                  <CircularProgress size={24} />
                ) : step === 'request'
                  ? 'Send OTP'
                  : 'Reset Password'}
              </Button>
            ) : (
              <Button
                type="button"
                fullWidth
                variant="contained"
                sx={{ mt: 2, mb: 1 }}
                onClick={handleBackToLogin}
              >
                Back to Login
              </Button>
            )}

            {step === 'verify' && (
              <>
                <Button
                  type="button"
                  fullWidth
                  variant="text"
                  onClick={handleResendOtp}
                  sx={{ textTransform: 'none', color: '#DD2424', fontWeight: 600 }}
                  disabled={requestLoading || verifyLoading}
                >
                  Resend OTP
                </Button>
                <Button
                  type="button"
                  fullWidth
                  variant="text"
                  onClick={handleChangeEmail}
                  sx={{ textTransform: 'none', color: '#DD2424', fontWeight: 600 }}
                  disabled={requestLoading || verifyLoading}
                >
                  Use a different email
                </Button>
              </>
            )}

            {step !== 'request' && step !== 'done' && (
              <Button
                type="button"
                fullWidth
                variant="text"
                onClick={handleBackToLogin}
                sx={{ textTransform: 'none', color: '#DD2424', fontWeight: 600 }}
                disabled={requestLoading || verifyLoading}
              >
                Back to Login
              </Button>
            )}
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ForgotPassword;
